<template>
  <div
    v-if="showPoweredBy && globalConfig.brandName && !disableBranding"
    id="branding"
    class="px-0 py-3 flex justify-center fixed bottom-0 w-full"
    :class="[
      $dm(hasToolbar ? 'bg-white' : 'bg-slate-50', 'dark:bg-slate-900'),
      { 'fixed bottom-0 w-full': $route.name === 'home' },
    ]"
  >
    <a
      :href="brandRedirectURL"
      rel="noreferrer noopener nofollow"
      target="_blank"
      class="branding--link justify-center"
    >
      <span
        class="flex items-center gap-2"
        :class="$dm('text-black-700', 'dark:text-slate-50')"
      >
        {{ useInstallationName($t('POWERED_BY'), globalConfig.brandName) }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="56"
          height="18"
          fill="none"
        >
          <g fill="#623BEA" clip-path="url(#a)">
            <path
              d="M9.87 7.718v5.664a.723.723 0 0 1-.714.722H7.713a1.12 1.12 0 0 1-1.128-1.12v-4.37c0-1.195-.728-1.755-1.643-1.755-.979 0-1.643.61-1.643 1.755v4.83c0 .361-.301.66-.665.66h-1.53A1.101 1.101 0 0 1 0 13.008V1.12C0 .498.502 0 1.129 0H2.62c.364 0 .665.299.665.66v4.157c.564-.66 1.467-1.17 2.797-1.17 2.596 0 3.788 2.017 3.788 4.07ZM22.25 9.013c0 3.024-2.296 5.365-5.532 5.365-3.21 0-5.53-2.34-5.53-5.365 0-3.05 2.32-5.366 5.53-5.366 3.236 0 5.532 2.316 5.532 5.366Zm-3.311 0c0-1.195-.878-2.204-2.22-2.204-1.317 0-2.195 1.009-2.195 2.204 0 1.195.878 2.178 2.194 2.178 1.355 0 2.22-.983 2.22-2.178ZM33.876 9.013c0 3.024-2.295 5.365-5.53 5.365-3.212 0-5.532-2.34-5.532-5.365 0-3.05 2.32-5.366 5.531-5.366 3.236 0 5.531 2.316 5.531 5.366Zm-3.311 0c0-1.195-.878-2.204-2.22-2.204-1.317 0-2.195 1.009-2.195 2.204 0 1.195.878 2.178 2.195 2.178 1.342 0 2.22-.983 2.22-2.178ZM50.519 3.896h2.07c.388 0 .652.386.501.747-.853 2.117-3.248 8.204-3.8 9.473-.815 1.917-1.768 3.897-4.139 3.897-.3 0-.564-.025-.815-.038a.542.542 0 0 1-.464-.547v-1.693c0-.287.238-.523.527-.548.99-.025 1.505-.585 1.893-1.668-.138-.361-2.847-6.672-3.775-8.876-.163-.348.1-.747.502-.747h2.032c.389 0 .727.237.853.61l1.868 5.179 1.894-5.191a.893.893 0 0 1 .853-.598ZM54.558 14.365c.796 0 1.442-.64 1.442-1.431s-.646-1.432-1.442-1.432c-.797 0-1.443.64-1.443 1.431s.646 1.432 1.443 1.432ZM40.987 3.71a4.006 4.006 0 0 0-2.621 1.73v-.884a.667.667 0 0 0-.665-.66h-1.919a.712.712 0 0 0-.714.71V13.419c0 .386.313.71.714.71h1.919a.667.667 0 0 0 .665-.66V9.91c.025-1.544.364-2.963 2.684-2.813a.555.555 0 0 0 .59-.548V4.233c0-.337-.327-.585-.653-.523Z"
            />
          </g>
        </svg>
      </span>
    </a>
  </div>
  <div v-else class="p-3" />
</template>

<script>
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import darkMixin from 'widget/mixins/darkModeMixin.js';

const {
  LOGO_THUMBNAIL: logoThumbnail,
  BRAND_NAME: brandName,
  WIDGET_BRAND_URL: widgetBrandURL,
} = window.globalConfig || {};

export default {
  mixins: [globalConfigMixin, darkMixin],
  props: {
    disableBranding: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      globalConfig: {
        brandName,
        logoThumbnail,
        widgetBrandURL,
      },
    };
  },
  computed: {
    showPoweredBy() {
      const widgetSettings = JSON.parse(window.hooryWebChannel.widget_settings);
      return widgetSettings?.showPoweredBy ?? true;
    },
    brandRedirectURL() {
      try {
        const referrerHost = this.$store.getters['appConfig/getReferrerHost'];
        const baseURL = `${this.globalConfig.widgetBrandURL}?utm_source=${
          referrerHost ? 'widget_branding' : 'survey_branding'
        }`;
        if (referrerHost) {
          return `${baseURL}&utm_referrer=${referrerHost}`;
        }
        return baseURL;
      } catch (e) {
        // Suppressing the error as getter is not defined in some cases
      }
      return '';
    },
    hasToolbar() {
      return window.hooryWebChannel && !!window.hooryWebChannel.portalId;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~widget/assets/scss/variables.scss';

.branding--image {
  margin-right: $space-smaller;
  max-width: $space-slab;
  max-height: $space-slab;
}

.branding--link {
  color: $color-light-gray;
  cursor: pointer;
  display: inline-flex;
  font-size: $font-size-small;
  opacity: 0.9;
  text-decoration: none;

  &:hover {
    filter: grayscale(0);
    opacity: 1;
    color: $color-gray;
  }
}
</style>
